import React from 'react'
import {
  PiPencilSimple,
  PiCopySimple,
  PiLightning,
  PiLightningSlash,
  PiTrash,
  PiPause,
  PiPlay,
  PiArrowsClockwise,
  PiArrowSquareOut,
  PiFolderOpen,
  PiDownloadSimple,
  PiPushPin,
  PiPushPinSlash,
} from 'react-icons/pi'
import { Ellipsis, EllipsisVertical } from 'lucide-react'
import { cn } from '../UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
  ds2026DropdownItemDestructiveClass,
  ds2026DropdownSeparatorClass,
} from '../FbUI/ds2026DropdownMenu'

export interface ActionItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  onClick: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
  disabledReason?: string
  hasSeparatorAfter?: boolean
  /** When provided, renders as a nested submenu instead of a regular menu item. */
  subContent?: React.ReactNode
}

interface ActionsDropdownProps {
  actions: ActionItem[]
  disabled?: boolean
  className?: string
  /** Extra classes for the trigger button itself (e.g. a hover background). */
  triggerClassName?: string
  /** Override the trigger icon. Defaults to the vertical three-dot ellipsis. */
  triggerIcon?: React.ComponentType<{
    size?: number
    className?: string
    strokeWidth?: number
  }>
  /** Extra classes for the menu panel itself (e.g. a border + tighter shadow). */
  contentClassName?: string
  containerWidth?: number
  /** Trigger glyph orientation. Defaults to vertical (existing behavior). */
  orientation?: 'vertical' | 'horizontal'
}

export function ActionsDropdown({
  actions,
  className,
  triggerClassName,
  triggerIcon,
  contentClassName,
  disabled,
  containerWidth = 120,
  orientation = 'vertical',
}: ActionsDropdownProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )
  // Caller-supplied icon wins; otherwise fall back to the orientation default
  // (horizontal ⋯ vs vertical ⋮).
  const TriggerIcon =
    triggerIcon ?? (orientation === 'horizontal' ? Ellipsis : EllipsisVertical)

  return (
    <div className={cn('relative flex justify-end', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            data-actions-trigger="true"
            className={cn(
              // Gray bg on hover AND while the menu is open (data-state=open) so
              // the trigger stays highlighted — you don't lose context when the
              // menu covers nearby content.
              'flex items-center justify-center w-5 h-5 rounded text-fg-secondary transition-colors hover:bg-gray-100 hover:text-fg focus:outline-none focus-visible:text-fg data-[state=open]:bg-gray-100 data-[state=open]:text-fg',
              {
                'opacity-50 cursor-not-allowed': disabled,
              },
              triggerClassName
            )}
            aria-label="Actions"
            onClick={(e) => e.stopPropagation()}
          >
            <TriggerIcon
              className={cn({
                'cursor-not-allowed': disabled,
              })}
              size={20}
              strokeWidth={2.5}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          style={{ minWidth: Math.max(containerWidth, 160), zIndex }}
          className={cn(ds2026DropdownContentClass, contentClassName)}
        >
          {actions.map((action, index) => {
            const isDestructive = action.variant === 'destructive'
            const previousIsDestructive =
              index > 0 && actions[index - 1]?.variant === 'destructive'
            // Auto-insert a divider before the first destructive item so
            // "Delete" sits in its own section without the caller having to
            // set hasSeparatorAfter on the previous action. Skip when the
            // destructive item is the only/first one — there's nothing above
            // it to separate from.
            const needsSeparatorBefore =
              isDestructive && index > 0 && !previousIsDestructive
            return (
              <React.Fragment key={index}>
                {needsSeparatorBefore && (
                  <DropdownMenuSeparator
                    className={ds2026DropdownSeparatorClass}
                  />
                )}
                {action.subContent && !action.disabled ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={ds2026DropdownItemClass}>
                      <action.icon
                        size={18}
                        className="flex-shrink-0 text-current"
                      />
                      <span className="truncate">{action.label}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      className={ds2026DropdownContentClass}
                    >
                      {action.subContent}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuItem
                    disabled={action.disabled}
                    title={action.disabled ? action.disabledReason : undefined}
                    className={cn(
                      isDestructive
                        ? ds2026DropdownItemDestructiveClass
                        : ds2026DropdownItemClass,
                      action.disabled && '!opacity-50 !cursor-not-allowed'
                    )}
                    onSelect={() => {
                      if (!action.disabled) {
                        action.onClick()
                      }
                    }}
                  >
                    <action.icon
                      size={18}
                      className="flex-shrink-0 text-current"
                    />
                    <span className="truncate">{action.label}</span>
                  </DropdownMenuItem>
                )}
                {action.hasSeparatorAfter && (
                  <DropdownMenuSeparator
                    className={ds2026DropdownSeparatorClass}
                  />
                )}
              </React.Fragment>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Pre-configured actions for common use cases
export const createEditAction = (
  onClick: () => void,
  label = 'Edit'
): ActionItem => ({
  label,
  icon: PiPencilSimple,
  onClick,
})

export const createCopyAction = (onClick: () => void): ActionItem => ({
  label: 'Copy',
  icon: PiCopySimple,
  onClick,
})

export const createPauseAction = (onClick: () => void): ActionItem => ({
  label: 'Pause',
  icon: PiPause,
  onClick,
})

export const createPlayAction = (onClick: () => void): ActionItem => ({
  label: 'Resume',
  icon: PiPlay,
  onClick,
})

export const createSyncAction = (
  onClick: () => void,
  disabled?: boolean
): ActionItem => ({
  label: 'Sync',
  icon: PiArrowsClockwise,
  onClick,
  disabled,
})

export const createExternalAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: PiArrowSquareOut,
  onClick,
})

export const createDownloadAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: PiDownloadSimple,
  onClick,
})

export const createToggleActiveAction = (
  onClick: () => void,
  isActive: boolean,
  isDisabled = false
): ActionItem => ({
  label: isActive ? 'Disable' : 'Enable',
  icon: isActive ? PiLightningSlash : PiLightning,
  onClick,
  disabled: isDisabled,
})

export const createDeleteAction = (onClick: () => void): ActionItem => ({
  label: 'Delete',
  icon: PiTrash,
  onClick,
  variant: 'destructive' as const,
})

export const createManageFilesAction = (onClick: () => void): ActionItem => ({
  label: 'Manage Files',
  icon: PiFolderOpen,
  onClick,
})

export const createPinToHomepageAction = (
  onClick: () => void,
  isPinned = false
): ActionItem => ({
  label: isPinned ? 'Unpin from homepage' : 'Pin to homepage',
  icon: isPinned ? PiPushPinSlash : PiPushPin,
  onClick,
})
